import { supabase } from '../lib/supabase';

export type CleaningSession = {
  id: string;
  user_id: string;
  location: string;
  started_at: string;
  completed_at?: string;
  status: 'active' | 'completed' | 'abandoned';
  duration_minutes?: number;
  points_earned?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  caption?: string;
  is_public?: boolean;
}

export interface SessionPhoto {
  id: string;
  session_id: string;
  type: 'before' | 'after';
  storage_url: string;
  order_index: number;
  created_at?: string;
}

export interface PhotoPair {
  id: string;
  session_id: string;
  before_id: string;
  after_id: string;
  order_index: number;
  beforePhoto?: SessionPhoto;
  afterPhoto?: SessionPhoto;
}

class SessionService {
  // Create a new cleaning session
  async createSession(location: string = 'Campus'): Promise<CleaningSession | null> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('cleaning_sessions')
        .insert({
          user_id: userData.user.id,
          location,
          difficulty: 'medium',
          is_public: true
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return null;
    }
  }

  // Get active session for current user
  async getActiveSession(): Promise<CleaningSession | null> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) return null;

      const { data, error } = await supabase
        .from('cleaning_sessions')
        .select('*')
        .eq('user_id', userData.user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (error) {
      return null;
    }
  }

  // Get session by ID
  async getSession(sessionId: string): Promise<CleaningSession | null> {
    try {
      const { data, error } = await supabase
        .from('cleaning_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return null;
    }
  }

  // Upload photo and save to session
  async uploadSessionPhoto(
    sessionId: string, 
    photoData: string, 
    type: 'before' | 'after',
    orderIndex: number
  ): Promise<SessionPhoto | null> {
    try {
      // Convert base64 to blob
      const base64Data = photoData.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });

      // Upload to Supabase Storage
      const fileName = `${sessionId}/${type}_${orderIndex}_${Date.now()}.jpg`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('photos')
        .upload(fileName, blob, {
          contentType: 'image/jpeg',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('photos')
        .getPublicUrl(fileName);

      // Save photo record
      
      const { data, error } = await supabase
        .from('photos')
        .insert({
          session_id: sessionId,
          type,
          storage_url: publicUrl,
          order_index: orderIndex
        })
        .select()
        .single();

      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      return null;
    }
  }

  // Get all photos for a session
  async getSessionPhotos(sessionId: string): Promise<SessionPhoto[]> {
    try {
      
      const { data, error } = await supabase
        .from('photos')
        .select('*')
        .eq('session_id', sessionId)
        .order('order_index', { ascending: true });

      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      return [];
    }
  }

  // Create photo pair
  async createPhotoPair(
    sessionId: string,
    beforeId: string,
    afterId: string,
    orderIndex: number
  ): Promise<PhotoPair | null> {
    try {
      
      const { data, error } = await supabase
        .from('photo_pairs')
        .insert({
          session_id: sessionId,
          before_id: beforeId,
          after_id: afterId,
          order_index: orderIndex
        })
        .select()
        .single();

      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      return null;
    }
  }

  // Get all photo pairs for a session with photo data
  async getSessionPhotoPairs(sessionId: string): Promise<PhotoPair[]> {
    try {
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // First try with joins
      let { data, error } = await supabase
        .from('photo_pairs')
        .select(`
          *,
          beforePhoto:photos!photo_pairs_before_id_fkey(*),
          afterPhoto:photos!photo_pairs_after_id_fkey(*)
        `)
        .eq('session_id', sessionId)
        .order('order_index', { ascending: true });

      
      // Check if we got the photo data
      const needsManualFetch = error || !data || data.length === 0 || !data[0].beforePhoto;
      
      if (needsManualFetch) {
        
        // Try without joins
        const { data: pairsOnly, error: pairsError } = await supabase
          .from('photo_pairs')
          .select('*')
          .eq('session_id', sessionId)
          .order('order_index', { ascending: true });
          
        
        if (pairsError) {
          throw pairsError;
        }
        
        
        // Manually fetch photos
        if (pairsOnly && pairsOnly.length > 0) {
          const photoIds = new Set<string>();
          pairsOnly.forEach(pair => {
            photoIds.add(pair.before_id);
            photoIds.add(pair.after_id);
          });
          
          const { data: photos, error: photosError } = await supabase
            .from('photos')
            .select('*')
            .in('id', Array.from(photoIds));
            
          
          const photoMap = new Map(photos?.map(p => [p.id, p]) || []);
          
          data = pairsOnly.map(pair => {
            const beforePhoto = photoMap.get(pair.before_id);
            const afterPhoto = photoMap.get(pair.after_id);
            return {
              ...pair,
              beforePhoto,
              afterPhoto
            };
          });
        } else {
          data = pairsOnly;
        }
      }
      
      return data || [];
    } catch (error) {
      return [];
    }
  }

  // Complete session with additional data
  async completeSession(
    sessionId: string,
    updates?: {
      duration_minutes?: number;
      points_earned?: number;
      difficulty?: 'easy' | 'medium' | 'hard';
      caption?: string;
      is_public?: boolean;
    }
  ): Promise<boolean> {
    try {
      const updateData: any = {
        status: 'completed',
        completed_at: new Date().toISOString()
      };
      
      if (updates) {
        Object.assign(updateData, updates);
      }
      
      const { error } = await supabase
        .from('cleaning_sessions')
        .update(updateData)
        .eq('id', sessionId);

      if (error) throw error;
      return true;
    } catch (error) {
      return false;
    }
  }

  // Post session to activity feed with points calculation
  async postToActivityFeed(
    sessionId: string,
    caption: string,
    isPrivate: boolean = false,
    difficulty: 'easy' | 'medium' | 'hard' = 'medium'
  ): Promise<{ success: boolean; points?: number; activityId?: string }> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) throw new Error('No authenticated user');

      // Get session details without waiting for auto-pair
      const session = await this.getSession(sessionId);
      if (!session) throw new Error('Session not found');
      
      // Start auto-pairing in background but don't wait
      const autoPairPromise = this.autoPairPhotos(sessionId);
      
      // Get photo pairs immediately - they might already be paired
      let photoPairs = await this.getSessionPhotoPairs(sessionId);
      
      // Wait for auto-pair to complete if no pairs exist yet
      if (photoPairs.length === 0) {
        await autoPairPromise;
        // Re-fetch pairs after auto-pairing
        photoPairs = await this.getSessionPhotoPairs(sessionId);
      }
      
      // Get first photo pair for backward compatibility
      const firstPair = photoPairs[0];
      
      // Calculate points based on photo pairs and difficulty
      const basePairs = photoPairs.length;
      const difficultyMultiplier = difficulty === 'easy' ? 0.8 : difficulty === 'hard' ? 1.5 : 1;
      const pointsEarned = Math.round(basePairs * 10 * difficultyMultiplier);
      
      // Create activity with first image for backward compatibility
      const { data: activityData, error: activityError } = await supabase
        .from('activities')
        .insert({
          profile_id: userData.user.id,
          user_id: userData.user.id,
          session_id: sessionId,
          activity_type: 'before-after',
          caption: caption || `Completed cleaning at ${session.location}`,
          location: session.location,
          is_private: isPrivate,
          description: caption || `Completed cleaning at ${session.location}`,
          before_image: firstPair?.beforePhoto?.storage_url || null,
          after_image: firstPair?.afterPhoto?.storage_url || null,
          before_photo_url: firstPair?.beforePhoto?.storage_url || null,
          after_photo_url: firstPair?.afterPhoto?.storage_url || null,
          points_awarded: pointsEarned,
          points_earned: pointsEarned,
          status: 'pending'
          // Removed photo_count - doesn't exist in database
        })
        .select('id')
        .single();

      if (activityError) throw activityError;
      
      // Store all photo pairs in a separate table for the activity
      // Commented out - table might not exist
      /*
      if (activityData && photoPairs.length > 0) {
        const activityPhotos = photoPairs.map((pair, index) => ({
          activity_id: activityData.id,
          before_photo_url: pair.beforePhoto?.storage_url,
          after_photo_url: pair.afterPhoto?.storage_url,
          order_index: index
        }));
        
        // Insert activity photos without waiting
        supabase
          .from('activity_photos')
          .insert(activityPhotos)
          .then(({ error }) => {
          });
      }
      */
      
      // Update session with points in background
      supabase
        .from('cleaning_sessions')
        .update({ points_earned: pointsEarned })
        .eq('id', sessionId)
        .then(({ error }) => {
        });
      
      return { success: true, points: pointsEarned, activityId: activityData?.id };
    } catch (error) {
      return { success: false };
    }
  }

  // Abandon session
  async abandonSession(sessionId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('cleaning_sessions')
        .update({ status: 'abandoned' })
        .eq('id', sessionId);

      if (error) throw error;
      return true;
    } catch (error) {
      return false;
    }
  }

  // Bulk photo upload - optimized version
  async uploadMultiplePhotos(
    sessionId: string,
    photos: Array<{data: string, type: 'before' | 'after'}>,
    startIndex: number = 0
  ): Promise<SessionPhoto[]> {
    try {
      // Process all photos in parallel for storage upload
      const uploadPromises = photos.map(async (photo, index) => {
        const base64Data = photo.data.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });

        const fileName = `${sessionId}/${photo.type}_${startIndex + index}_${Date.now()}_${index}.jpg`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('photos')
          .upload(fileName, blob, {
            contentType: 'image/jpeg',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('photos')
          .getPublicUrl(fileName);

        return {
          session_id: sessionId,
          type: photo.type,
          storage_url: publicUrl,
          order_index: startIndex + index
        };
      });

      const photoDataArray = await Promise.all(uploadPromises);

      // Batch insert all photo records at once
      const { data, error } = await supabase
        .from('photos')
        .insert(photoDataArray)
        .select();

      if (error) throw error;
      return data || [];
    } catch (error) {
      // Fall back to individual uploads
      const uploadPromises = photos.map((photo, index) => 
        this.uploadSessionPhoto(sessionId, photo.data, photo.type, startIndex + index)
      );
      const results = await Promise.all(uploadPromises);
      return results.filter((r): r is SessionPhoto => r !== null);
    }
  }

  // Auto pair photos
  async autoPairPhotos(sessionId: string): Promise<PhotoPair[]> {
    
    const photos = await this.getSessionPhotos(sessionId);
    
    const beforePhotos = photos.filter(p => p.type === 'before').sort((a, b) => a.order_index - b.order_index);
    const afterPhotos = photos.filter(p => p.type === 'after').sort((a, b) => a.order_index - b.order_index);
    
    
    // Get existing pairs to avoid duplicates
    const existingPairs = await this.getSessionPhotoPairs(sessionId);
    
    const pairedBeforeIds = new Set(existingPairs.map(p => p.before_id));
    const pairedAfterIds = new Set(existingPairs.map(p => p.after_id));
    
    // Find unpaired photos
    const unpairedBefore = beforePhotos.filter(p => !pairedBeforeIds.has(p.id));
    const unpairedAfter = afterPhotos.filter(p => !pairedAfterIds.has(p.id));
    
    
    const pairs = [];
    const pairsToCreate = Math.min(unpairedBefore.length, unpairedAfter.length);
    
    for (let i = 0; i < pairsToCreate; i++) {
      const pair = await this.createPhotoPair(
        sessionId,
        unpairedBefore[i].id,
        unpairedAfter[i].id,
        existingPairs.length + pairs.length
      );
      if (pair) {
        pairs.push(pair);
      } else {
      }
    }
    return pairs;
  }

  // Get session progress
  async getSessionProgress(sessionId: string): Promise<{
    beforeCount: number;
    afterCount: number;
    pairCount: number;
    canComplete: boolean;
  }> {
    const photos = await this.getSessionPhotos(sessionId);
    const pairs = await this.getSessionPhotoPairs(sessionId);
    const beforeCount = photos.filter(p => p.type === 'before').length;
    const afterCount = photos.filter(p => p.type === 'after').length;
    
    return {
      beforeCount,
      afterCount,
      pairCount: pairs.length,
      canComplete: pairs.length > 0
    };
  }

  // Get all photo pairs for an activity - DEPRECATED, use getSessionPhotoPairs directly
  async getActivityPhotos(activityId: string): Promise<Array<{before_photo_url: string; after_photo_url: string; order_index: number}>> {
    try {
      // Get activity to find session_id
      const { data: activity, error } = await supabase
        .from('activities')
        .select('session_id')
        .eq('id', activityId)
        .single();
      
      if (error || !activity?.session_id) {
        return [];
      }
      
      // Get photos from session
      const pairs = await this.getSessionPhotoPairs(activity.session_id);
      return pairs.map((pair, index) => ({
        before_photo_url: pair.beforePhoto?.storage_url || '',
        after_photo_url: pair.afterPhoto?.storage_url || '',
        order_index: index
      }));
    } catch (error) {
      return [];
    }
  }

  // Debug function to check session state
  async debugSession(sessionId: string) {
    
    // Get all photos
    const photos = await this.getSessionPhotos(sessionId);
    
    // Get all pairs
    const pairs = await this.getSessionPhotoPairs(sessionId);
    
    // Check for orphaned photos
    const pairedPhotoIds = new Set();
    pairs.forEach(pair => {
      pairedPhotoIds.add(pair.before_id);
      pairedPhotoIds.add(pair.after_id);
    });
    
    const orphanedPhotos = photos.filter(p => !pairedPhotoIds.has(p.id));
    
    return {
      photos,
      pairs,
      orphanedPhotos
    };
  }
}

export const sessionService = new SessionService();